/**
 * Org settings mapping for the organization form: what the form reads from the
 * org payload and what it sends back as subscription_information.
 */

/**
 * Reads org settings: MFA and VQA only from the org root only; trial/chat from
 * subscription_information (with legacy top-level fallbacks for feature flags).
 */
export function getOrgSubscriptionSettingsFromApi(org) {
  const sub = org?.subscription_information;
  return {
    mfa_required_for_org: Boolean(org?.mfa_required_for_org),
    is_vqa_only: Boolean(org?.is_vqa_only),
    trial_experience_enabled: Boolean(
      sub?.has_trial_experience ?? org?.trial_experience_enabled,
    ),
    chat_diligent_enabled: Boolean(
      sub?.has_chat_diligent ?? org?.chat_diligent_enabled,
    ),
    has_auto_score: sub?.has_auto_score ?? true,
    auto_score_quota: sub?.auto_score_quota ?? null,
    auto_score_unlimited: sub?.auto_score_unlimited ?? true,
    auto_score_used: sub?.auto_score_used ?? 0,
    has_sme_score: sub?.has_sme_score ?? false,
    sme_score_quota: sub?.sme_score_quota ?? null,
    sme_score_unlimited: sub?.sme_score_unlimited ?? false,
    sme_score_used: sub?.sme_score_used ?? 0,
  };
}

/**
 * Builds subscription_information for PATCH: spreads existing API fields, then
 * updates risk entitlements (always) plus optional trial/chat flags (sponsor only).
 * MFA is sent on the payload root, not here.
 *
 * includeFeatureFlags gates the sponsor-only trial/chat flags; vendors never
 * receive those. Risk entitlements are sent for both org types, the provider
 * columns stay in place even though vendors no longer see risk scoring.
 */
export function buildSubscriptionInformationPatch(org, orgData, options = {}) {
  const { includeFeatureFlags = true } = options;
  const existing =
    org?.subscription_information &&
    typeof org.subscription_information === "object"
      ? { ...org.subscription_information }
      : {};
  const riskEntitlements = {
    has_auto_score: !!orgData.has_auto_score,
    auto_score_quota: orgData.auto_score_quota,
    auto_score_unlimited: !!orgData.auto_score_unlimited,
    has_sme_score: !!orgData.has_sme_score,
    sme_score_quota: orgData.sme_score_quota,
    sme_score_unlimited: !!orgData.sme_score_unlimited,
  };
  if (!includeFeatureFlags) {
    return { ...existing, ...riskEntitlements };
  }
  return {
    ...existing,
    ...riskEntitlements,
    has_trial_experience: !!orgData.trial_experience_enabled,
    has_chat_diligent: !!orgData.chat_diligent_enabled,
  };
}

const LISTED_FAILED_USERS = 5;
const FAILURE_REASON_LENGTH = 100;

function describeFailedUser(user) {
  const who = user?.email || "a user without an email";
  const reason = String(user?.message ?? "").trim();
  if (!reason) return who;
  const shortReason = reason.length > FAILURE_REASON_LENGTH ? `${reason.slice(0, FAILURE_REASON_LENGTH)}…` : reason;
  return `${who} (${shortReason})`;
}

/**
 * What to tell the admin after saving a sponsor's settings. Turning VQA only off gives the users
 * HyperComply access; the ones that could not get it are listed with the reason, so the admin
 * knows to save again.
 * @param {{ hypercomply_failed_users?: Array<{ email?: string, message?: string }> } | string | undefined} result
 * @returns {{ isSuccess: boolean, message: string }}
 */
export function describeSettingsSaveResult(result) {
  const failed = Array.isArray(result?.hypercomply_failed_users) ? result.hypercomply_failed_users : [];
  if (failed.length === 0) {
    return { isSuccess: true, message: "Organization settings saved successfully." };
  }
  const listed = failed.slice(0, LISTED_FAILED_USERS).map(describeFailedUser);
  const unlisted = failed.length - listed.length;
  const users = unlisted > 0 ? `${listed.join("; ")}; and ${unlisted} more` : listed.join("; ");
  const count = failed.length === 1 ? "1 user" : `${failed.length} users`;
  return {
    isSuccess: false,
    message: `Organization settings saved, but ${count} could not be given HyperComply access: ${users}. Save the settings again to retry.`,
  };
}

/** The reason a settings save failed: the server's own words when it gave any. */
export function settingsSaveErrorMessage(error) {
  const data = error?.response?.data;
  const serverMessage = typeof data === "string" ? data : data?.message;
  if (typeof serverMessage === "string" && serverMessage.trim()) return serverMessage.trim();
  return error?.message || "Error saving organization settings.";
}

/**
 * Whether saving these settings switches a VQA only org back, which creates its HyperComply
 * organization and accounts and cannot be undone.
 */
export function isTurningVqaOnlyOff(savedOrg, orgData) {
  return Boolean(savedOrg?.is_vqa_only) && !orgData?.is_vqa_only;
}

/** Sponsor settings that live on the PATCH payload root rather than in subscription_information. */
export function buildSponsorSettingsRootFields(orgData) {
  return {
    mfa_required_for_org: !!orgData?.mfa_required_for_org,
    is_vqa_only: !!orgData?.is_vqa_only,
  };
}
